import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, PrimaryButton, Modal, Field, TextInput, useFetch } from "../../components/crm/ui";
import { useState } from "react";
import { Plus, Trash2, Edit3 } from "lucide-react";

function buildPage({ title, overline, path, columns, fieldDefs, defaultForm, testidPrefix }) {
  return function ListPage() {
    const { data, reload } = useFetch(path);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(defaultForm);
    const [saving, setSaving] = useState(false);

    function startCreate() { setEditing(null); setForm(defaultForm); setOpen(true); }
    function startEdit(item) { setEditing(item); setForm({ ...defaultForm, ...item }); setOpen(true); }

    async function save(e) {
      e.preventDefault();
      setSaving(true);
      try {
        if (editing) await api.put(`${path}/${editing.id}`, form);
        else await api.post(path, form);
        toast.success(editing ? "Updated" : "Created");
        setOpen(false); reload();
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally { setSaving(false); }
    }

    async function remove(item) {
      if (!window.confirm(`Delete ${item.name}?`)) return;
      await api.delete(`${path}/${item.id}`); toast.success("Deleted"); reload();
    }

    return (
      <div data-testid={`${testidPrefix}-page`}>
        <PageHeader overline={overline} title={title}
          action={<PrimaryButton onClick={startCreate} testid={`create-${testidPrefix}-btn`}><Plus className="w-4 h-4" /> New</PrimaryButton>} />
        <div className="border border-[#E2E4DC] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase tracking-[0.25em] text-[#4A5568] border-b border-[#E2E4DC]">
                {columns.map((c) => <th key={c.key} className="px-4 py-3">{c.label}</th>)}
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((item) => (
                <tr key={item.id} className="border-b border-[#E2E4DC] hover:bg-[#F4F5F0]">
                  {columns.map((c) => <td key={c.key} className="px-4 py-3 font-mono text-sm">{c.render ? c.render(item) : item[c.key] || "—"}</td>)}
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => startEdit(item)} className="p-1.5 hover:text-[#1F4E2C]"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => remove(item)} className="p-1.5 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-sm text-[#4A5568]">No records yet.</td></tr>}
            </tbody>
          </table>
        </div>

        <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Edit ${title}` : `New ${title}`}>
          <form onSubmit={save} className="space-y-4">
            {fieldDefs.map((f) => (
              <Field key={f.key} label={f.label}>
                <TextInput
                  type={f.type || "text"}
                  required={!!f.required}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  data-testid={`${testidPrefix}-${f.key}`}
                />
              </Field>
            ))}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-[#4A5568]">Cancel</button>
              <PrimaryButton type="submit" disabled={saving} testid={`${testidPrefix}-save-btn`}>{saving ? "Saving…" : "Save"}</PrimaryButton>
            </div>
          </form>
        </Modal>
      </div>
    );
  };
}

export const Vendors = buildPage({
  title: "Vendors", overline: "Master Data", path: "/vendors",
  testidPrefix: "vendor",
  columns: [
    { key: "name", label: "Name" }, { key: "contact_person", label: "Contact" },
    { key: "phone", label: "Phone" }, { key: "email", label: "Email" },
    { key: "gst_number", label: "GST" },
  ],
  fieldDefs: [
    { key: "name", label: "Name *", required: true },
    { key: "contact_person", label: "Contact Person" },
    { key: "phone", label: "Phone *", required: true },
    { key: "email", label: "Email *", type: "email", required: true },
    { key: "address", label: "Address" },
    { key: "gst_number", label: "GST Number" },
  ],
  defaultForm: { name: "", contact_person: "", phone: "", email: "", address: "", gst_number: "" },
});

export const SalesEmployees = buildPage({
  title: "Sales Employees", overline: "Team", path: "/sales-employees",
  testidPrefix: "salesemp",
  columns: [
    { key: "name", label: "Name" }, { key: "phone", label: "Phone" },
    { key: "email", label: "Email" }, { key: "territory", label: "Territory" },
  ],
  fieldDefs: [
    { key: "name", label: "Name *", required: true },
    { key: "phone", label: "Phone *", required: true },
    { key: "email", label: "Email *", type: "email", required: true },
    { key: "territory", label: "Territory" },
  ],
  defaultForm: { name: "", phone: "", email: "", territory: "" },
});

export const Technicians = buildPage({
  title: "Technicians", overline: "Field Team", path: "/technicians",
  testidPrefix: "tech",
  columns: [
    { key: "name", label: "Name" }, { key: "phone", label: "Phone" },
    { key: "email", label: "Email" }, { key: "skill", label: "Skill" },
  ],
  fieldDefs: [
    { key: "name", label: "Name *", required: true },
    { key: "phone", label: "Phone *", required: true },
    { key: "email", label: "Email *", type: "email", required: true },
    { key: "skill", label: "Skill" },
  ],
  defaultForm: { name: "", phone: "", email: "", skill: "" },
});

export const CustomerGroups = buildPage({
  title: "Customer Groups", overline: "Tags", path: "/customer-groups",
  testidPrefix: "cg",
  columns: [{ key: "name", label: "Name" }, { key: "description", label: "Description" }],
  fieldDefs: [
    { key: "name", label: "Name *", required: true },
    { key: "description", label: "Description" },
  ],
  defaultForm: { name: "", description: "" },
});

export const MaterialGroups = buildPage({
  title: "Material Groups", overline: "Tags", path: "/material-groups",
  testidPrefix: "mg",
  columns: [{ key: "name", label: "Name" }, { key: "description", label: "Description" }],
  fieldDefs: [
    { key: "name", label: "Name *", required: true },
    { key: "description", label: "Description" },
  ],
  defaultForm: { name: "", description: "" },
});
